/**
 * MarketFilterMultiselect — which bazaar to shop (STORY-003-005; restyled
 * 2026-08).
 *
 * There is NO public markets-list endpoint in this story's dependency set (only
 * `/marketplace/categories` and `/marketplace/search`), and inventing one is
 * forbidden — so the options are passed in as a STATIC list by the page, which
 * currently supplies an empty one. The empty case therefore renders as an
 * honest note ("all markets") rather than as a broken control: a checkbox group
 * with nothing in it would read as a loading failure.
 *
 * A labelled checkbox group rather than a popover combobox: `@radix-ui/react-popover`
 * is not a dependency, and a visible list of markets is better for this audience
 * anyway — the names are the physical places they already know (Уста,
 * Кудайберген, Ошский), and hiding them behind a disclosure buys nothing at
 * three-to-five items. Selecting multiple markets is an OR filter server-side.
 *
 * Each row is a full-width 44px `<label>` so the tap target is the row, not the
 * 16px box.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

/** One market option (id + display name). Supplied statically by the page. */
export interface MarketOption {
  id: string;
  name: string;
}

export interface MarketFilterMultiselectProps {
  options: readonly MarketOption[];
  selectedIds: readonly string[];
  onChange: (selectedIds: string[]) => void;
}

export function MarketFilterMultiselect({
  options,
  selectedIds,
  onChange,
}: MarketFilterMultiselectProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const selected = new Set(selectedIds);

  const toggle = (id: string): void => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <fieldset
      className="m-0 flex flex-col border-0 p-0"
      data-testid="market-filter"
    >
      <legend className="sr-only">{t("filters.market")}</legend>
      {options.length === 0 ? (
        <p className="text-sm text-[color:var(--g-ink-2)]">
          {t("filters.marketPlaceholder")}
        </p>
      ) : (
        options.map((market) => (
          <label
            key={market.id}
            className="flex min-h-[44px] cursor-pointer items-center gap-2.5 text-sm text-foreground"
          >
            <input
              type="checkbox"
              data-testid={`market-option-${market.id}`}
              checked={selected.has(market.id)}
              onChange={() => toggle(market.id)}
              className="size-4 shrink-0 rounded-[4px] border-[color:var(--g-rule-strong)] accent-[color:var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
            <span className="truncate">{market.name}</span>
          </label>
        ))
      )}
    </fieldset>
  );
}
